import React, { useEffect, useState } from 'react'
import { Input, Form as AntForm, Row, Col, Typography, Select, Switch } from 'antd'
import { FormikErrors, FormikTouched } from 'formik'
import cronstrue from 'cronstrue'

const { Option } = Select
const AntFormItem = AntForm.Item
const { Text, Paragraph } = Typography

// Interface for the schedule values this component deals with
interface ScheduleFormValues {
  channel: string
  params?: any
  tableName?: string
  schedule?: string
  scheduleEnabled?: boolean
}

interface ScheduleOptionsFormProps {
  values: ScheduleFormValues
  errors: FormikErrors<ScheduleFormValues>
  touched: FormikTouched<ScheduleFormValues>
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  handleChange: (e: React.ChangeEvent<any>) => void
  entrances?: { code: string; name: string; params?: any }[]
  disabled?: boolean
}

// Interface for the individual CRON parts
interface CronParts {
  minute: string;
  hour: string;
  dom: string; // Day of Month
  month: string;
  dow: string; // Day of Week
}

// Default CRON parts
const defaultCronParts: CronParts = { minute: '', hour: '', dom: '', month: '', dow: '' };

// Helper to parse a CRON string into parts (basic split)
const parseCronString = (cronString: string | undefined): CronParts => {
  if (!cronString) return defaultCronParts;
  const parts = cronString.split(' ');
  return {
    minute: parts[0] || '',
    hour: parts[1] || '',
    dom: parts[2] || '',
    month: parts[3] || '',
    dow: parts[4] || '',
  };
};

// Helper to join CRON parts back into a string
const joinCronParts = (parts: CronParts): string => {
  return `${parts.minute || ''} ${parts.hour || ''} ${parts.dom || ''} ${parts.month || ''} ${parts.dow || ''}`.trim();
};

const ScheduleOptionsForm: React.FC<ScheduleOptionsFormProps> = ({
  values,
  errors,
  touched,
  setFieldValue,
  handleChange,
  entrances = [],
  disabled = false
}) => {
  // State to hold the individual CRON parts for the custom UI
  const [cronParts, setCronParts] = useState<CronParts>(parseCronString(values.schedule));
  const [cronDescription, setCronDescription] = useState<string>('');

  // Effect to update individual parts when the main schedule value changes
  useEffect(() => {
    if (values.scheduleEnabled) {
      setCronParts(parseCronString(values.schedule));
    }
  }, [values.schedule, values.scheduleEnabled]);

  // Effect to update CRON description
  useEffect(() => {
    if (values.scheduleEnabled && values.schedule) {
      try {
        const description = cronstrue.toString(values.schedule);
        setCronDescription(description);
      } catch (err: any) {
        const errorMessage = `Invalid CRON syntax: ${err.message || 'Unknown error'}`;
        setCronDescription(errorMessage);
      }
    } else {
      setCronDescription('');
    }
  }, [values.schedule, values.scheduleEnabled]);

  // Effect to enable schedule when schedule has a value
  useEffect(() => {
    if (values.schedule && !values.scheduleEnabled) {
      setFieldValue('scheduleEnabled', true);
    }
  }, [values.schedule, values.scheduleEnabled, setFieldValue]);

  // Handler for changes in the individual CRON input fields
  const handleCronPartChange = (part: keyof CronParts, value: string) => {
    const newParts = { ...cronParts, [part]: value };
    setCronParts(newParts);
    setFieldValue('schedule', joinCronParts(newParts));
  };

  return (
    <div style={{ border: '1px solid #d9d9d9', padding: '15px', borderRadius: '2px' }}>
      <AntFormItem style={{ marginBottom: 8 }}>
        <Switch
          checked={values.scheduleEnabled}
          onChange={(checked) => {
            setFieldValue('scheduleEnabled', checked)
            if (!checked) {
              setFieldValue('schedule', '')
            }
          }}
          disabled={disabled}
        />
        <span style={{ marginLeft: 8, fontWeight: 'bold' }}>Enable Schedule</span>
      </AntFormItem>

      {values.scheduleEnabled && (
        <>
          <AntFormItem 
            label="Table Name" 
            validateStatus={errors.tableName && touched.tableName ? 'error' : ''}
            help={errors.tableName && touched.tableName ? errors.tableName : ''}
            style={{ marginBottom: 8 }}
            
          >
            <Input 
              name="tableName"
              value={values.tableName}
              onChange={handleChange}
              disabled={disabled}
            />
          </AntFormItem>

          <AntFormItem
            label="Schedule" 
            style={{ marginBottom: 8 }}
            validateStatus={errors.schedule && touched.schedule ? 'error' : ''}
          >
            <Row gutter={8} wrap={false} style={{ marginBottom: 10 }}>
              <Col flex="auto">
                <AntFormItem label="Minute" style={{ marginBottom: 0 }}>
                  <Input 
                    value={cronParts.minute} 
                    onChange={(e) => handleCronPartChange('minute', e.target.value)}
                    placeholder="*"
                    disabled={disabled}
                  />
                </AntFormItem>
              </Col>
              <Col flex="auto">
                <AntFormItem label="Hour" style={{ marginBottom: 0 }}>
                  <Input 
                    value={cronParts.hour} 
                    onChange={(e) => handleCronPartChange('hour', e.target.value)}
                    placeholder="*"
                    disabled={disabled}
                  />
                </AntFormItem>
              </Col>
              <Col flex="auto">
                <AntFormItem label="Day (Month)" style={{ marginBottom: 0 }}>
                  <Input 
                    value={cronParts.dom} 
                    onChange={(e) => handleCronPartChange('dom', e.target.value)}
                    placeholder="*"
                    disabled={disabled}
                  />
                </AntFormItem>
              </Col>
              <Col flex="auto">
                <AntFormItem label="Month" style={{ marginBottom: 0 }}>
                  <Input 
                    value={cronParts.month} 
                    onChange={(e) => handleCronPartChange('month', e.target.value)}
                    placeholder="*"
                    disabled={disabled}
                  />
                </AntFormItem>
              </Col>
              <Col flex="auto">
                <AntFormItem label="Day (Week)" style={{ marginBottom: 0 }}>
                  <Input 
                    value={cronParts.dow} 
                    onChange={(e) => handleCronPartChange('dow', e.target.value)}
                    placeholder="*"
                    disabled={disabled}
                  />
                </AntFormItem>
              </Col>
            </Row>

            {errors.schedule && touched.schedule && (
              <div style={{ color: 'red', fontSize: '12px', marginTop: '-5px', marginBottom: '10px' }}>
                {errors.schedule as string}
              </div>
            )}

            {cronDescription && (
              <Paragraph style={{ marginTop: 5, fontStyle: 'italic', color: cronDescription.startsWith('Invalid') ? 'red' : '#595959' }}>
                {cronDescription}
              </Paragraph>
            )}

            <Paragraph type="secondary" style={{ marginTop: 10, fontSize: '12px' }}>
              <Text strong>*</Text> any value 
              <Text strong>,</Text> value list separator 
              <Text strong>-</Text> range of values 
              <Text strong>/</Text> step values
            </Paragraph>
          </AntFormItem>
        </>
      )}

      <AntFormItem
        label="Entrance Channel"
        style={{ marginBottom: 8 }}
      >
        <Select
          style={{ width: '100%' }}
          value={values.channel}
          onChange={(channelValue) => {
            setFieldValue('channel', channelValue)
            if (entrances?.find((item) => item.code === channelValue)?.params) {
              setFieldValue('params', entrances?.find((item) => item.code === channelValue)?.params)
            } else {
              setFieldValue('params', [])
            }
          }}
          disabled={values.scheduleEnabled || disabled}
        >
          {entrances?.map((item: any) => (
            <Option key={item.code} value={item.code}>{item.name}</Option>
          ))}
        </Select>
      </AntFormItem>
    </div>
  )
}

export default ScheduleOptionsForm 